import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { useTheme } from '../lib/theme'
import { useSettingsEntries } from '../lib/settingsIndex'
import { BillingNotice } from './BillingNotice'
import { HELP_TOPICS } from '../lib/helpTopics'
import { CommandPalette, type PaletteItem } from './CommandPalette'
import { FeedbackPanel } from './FeedbackPanel'
import { PracticeBillingBanner, usePracticeSwitched } from './PracticeBillingBanner'
import { ImpersonationBanner } from './ImpersonationBanner'
import { SendingPausedBanner } from './SendingControl'
import { PracticeSwitcher } from './PracticeSwitcher'
import { ThemeToggle } from './ThemeToggle'
import { useRole } from '../lib/team'
import { Logo, LogoMark } from './Logo'
import { CookieSettingsLink } from './ConsentBanner'

// Inline SVG icons throughout -- no icon library in this app (matches
// TrendChart.tsx's hand-rolled-SVG precedent), kept as plain geometric
// primitives (rect/circle/line) rather than hand-authored path curves so
// there's no risk of a malformed `d` attribute rendering as a broken shape.
function IconBase({ children, className = 'size-5 shrink-0' }: { children: ReactNode; className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  )
}

const DashboardIcon = () => (
  <IconBase>
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </IconBase>
)
const RemindersIcon = () => (
  <IconBase>
    <rect x="4" y="3" width="16" height="18" rx="1.5" />
    <line x1="8" y1="8" x2="16" y2="8" />
    <line x1="8" y1="12" x2="16" y2="12" />
    <line x1="8" y1="16" x2="13" y2="16" />
  </IconBase>
)
const OutstandingContactsIcon = () => (
  <IconBase>
    <circle cx="9" cy="8" r="3" />
    <path d="M4 20c0-3.3 2.7-5.5 5.5-5.5S15 16.7 15 20" />
    <line x1="17" y1="8" x2="21" y2="8" />
    <line x1="17" y1="12" x2="21" y2="12" />
  </IconBase>
)
const RulesIcon = () => (
  <IconBase>
    <line x1="4" y1="7" x2="20" y2="7" />
    <circle cx="9" cy="7" r="2" />
    <line x1="4" y1="14" x2="20" y2="14" />
    <circle cx="16" cy="14" r="2" />
    <line x1="4" y1="21" x2="20" y2="21" />
    <circle cx="11" cy="21" r="2" />
  </IconBase>
)
const TemplatesIcon = () => (
  <IconBase>
    <rect x="3" y="4" width="18" height="13" rx="2" />
    <path d="M8 17l-2 4v-4" />
  </IconBase>
)
const ConnectionsIcon = () => (
  <IconBase>
    <circle cx="8" cy="12" r="4.5" />
    <circle cx="16" cy="12" r="4.5" />
  </IconBase>
)
const AnalyticsIcon = () => (
  <IconBase>
    <rect x="4" y="10" width="3.5" height="10" />
    <rect x="10.25" y="4" width="3.5" height="16" />
    <rect x="16.5" y="13" width="3.5" height="7" />
  </IconBase>
)
const FeedbackIcon = () => (
  <IconBase>
    <rect x="3" y="4" width="18" height="13" />
    <line x1="8" y1="21" x2="12" y2="17" />
  </IconBase>
)
const HelpIcon = () => (
  <IconBase>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 .9-1 1.7" />
    <line x1="12" y1="17" x2="12" y2="17.01" />
  </IconBase>
)
const SettingsIcon = () => (
  <IconBase>
    <circle cx="12" cy="12" r="3" />
    <circle cx="12" cy="12" r="8" />
  </IconBase>
)
const AdminIcon = () => (
  <IconBase>
    <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
  </IconBase>
)
const CustomersIcon = () => (
  <IconBase>
    <circle cx="9" cy="8" r="3" />
    <path d="M3 20c0-3.5 3-6 6-6s6 2.5 6 6" />
    <circle cx="17.5" cy="9" r="2.25" />
    <path d="M16 20c.2-2.3 1.6-4.1 3.5-5" />
  </IconBase>
)
const ContactInquiriesIcon = () => (
  <IconBase>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </IconBase>
)
const WaitlistIcon = () => (
  <IconBase>
    <circle cx="9" cy="8" r="3" />
    <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
    <line x1="17" y1="8" x2="21" y2="8" />
    <line x1="19" y1="6" x2="19" y2="10" />
  </IconBase>
)
const AdminActionsIcon = () => (
  <IconBase>
    <path d="M12 3l8 4v5c0 4.5-3.2 7.6-8 9-4.8-1.4-8-4.5-8-9V7z" />
    <line x1="12" y1="8" x2="12" y2="13" />
    <circle cx="12" cy="16" r="0.5" fill="currentColor" />
  </IconBase>
)
const BillingIcon = () => (
  <IconBase>
    <rect x="3" y="6" width="18" height="12" rx="2" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </IconBase>
)
const AdminAnalyticsIcon = () => (
  <IconBase>
    <circle cx="12" cy="12" r="8" />
    <path d="M12 4V12L18 16" />
  </IconBase>
)
const MessagingTestIcon = () => (
  <IconBase>
    <path d="M3 11l18-7-7 18-3-8-8-3z" />
  </IconBase>
)
const IntegrationLogsIcon = () => (
  <IconBase>
    <rect x="4" y="3" width="16" height="18" rx="1.5" />
    <path d="M8 8l1.5 1.5L12 7" />
    <line x1="14" y1="8" x2="17" y2="8" />
    <path d="M8 15l1.5 1.5L12 14" />
    <line x1="14" y1="15" x2="17" y2="15" />
  </IconBase>
)
const WhatsAppTemplateSubmissionsIcon = () => (
  <IconBase>
    <rect x="4" y="4" width="16" height="12" rx="2" />
    <path d="M8 20l2.5-4" />
    <line x1="8" y1="9" x2="16" y2="9" />
    <line x1="8" y1="12" x2="13" y2="12" />
  </IconBase>
)
const WhatsAppPlatformTemplatesIcon = () => (
  <IconBase>
    <rect x="4" y="4" width="16" height="12" rx="2" />
    <path d="M8 20l2.5-4" />
    <circle cx="9" cy="9" r="1" />
    <line x1="12" y1="9" x2="17" y2="9" />
    <circle cx="9" cy="13" r="1" />
    <line x1="12" y1="13" x2="15" y2="13" />
  </IconBase>
)
const ChannelsIcon = () => (
  <IconBase>
    <circle cx="6" cy="7" r="2" />
    <circle cx="6" cy="17" r="2" />
    <circle cx="18" cy="12" r="2" />
    <line x1="8" y1="7.7" x2="16" y2="11.3" />
    <line x1="8" y1="16.3" x2="16" y2="12.7" />
  </IconBase>
)
const VersionsIcon = () => (
  <IconBase>
    <rect x="4" y="4" width="13" height="4" rx="1" />
    <rect x="7" y="10" width="13" height="4" rx="1" />
    <rect x="10" y="16" width="10" height="4" rx="1" />
  </IconBase>
)
const WhatsAppNumbersIcon = () => (
  <IconBase>
    <rect x="3" y="4" width="10" height="16" rx="2" />
    <rect x="11" y="7" width="10" height="16" rx="2" />
    <line x1="6" y1="18" x2="10" y2="18" />
  </IconBase>
)
const TodaysRemindersIcon = () => (
  <IconBase>
    <rect x="3" y="4" width="18" height="17" rx="1.5" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="7" y1="2" x2="7" y2="5" />
    <line x1="17" y1="2" x2="17" y2="5" />
    <circle cx="12" cy="14.5" r="2" />
  </IconBase>
)
const CHEVRON_PATHS = {
  left: 'M15 6l-6 6 6 6',
  right: 'M9 6l6 6-6 6',
  up: 'M6 15l6-6 6 6',
  down: 'M6 9l6 6 6-6',
} as const

const ChevronIcon = ({ direction }: { direction: keyof typeof CHEVRON_PATHS }) => (
  <IconBase className="size-4 shrink-0">
    <path d={CHEVRON_PATHS[direction]} />
  </IconBase>
)
const MenuIcon = () => (
  <IconBase className="size-6">
    <path d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
  </IconBase>
)
const CloseIcon = () => (
  <IconBase className="size-6">
    <path d="M6 18 18 6M6 6l12 12" />
  </IconBase>
)
const SearchIcon = () => (
  <IconBase>
    <circle cx="11" cy="11" r="6.5" />
    <line x1="16" y1="16" x2="21" y2="21" />
  </IconBase>
)
const LogoutIcon = () => (
  <IconBase>
    <path d="M9 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h4" />
    <path d="M16 17l5-5-5-5" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </IconBase>
)

interface NavItem {
  to: string
  label: string
  icon: () => React.JSX.Element
  end?: boolean
}

interface NavGroup {
  key: string
  label: string
  icon: () => React.JSX.Element
  items: NavItem[]
}

// A nav entry that runs an action (opens the feedback panel) instead of
// navigating, so it has no `to`.
interface NavAction {
  action: 'feedback'
  label: string
  icon: () => React.JSX.Element
}

type NavEntry = NavItem | NavGroup | NavAction

function isGroup(entry: NavEntry): entry is NavGroup {
  return 'items' in entry
}

function isAction(entry: NavEntry): entry is NavAction {
  return 'action' in entry
}

// Grouped rather than one flat list of 15: at that length a flat menu is a
// wall of equally-weighted links, and on a phone most of it sits below the
// fold. The three most-used destinations stay top-level and everything
// else collapses under the job it belongs to.
const navItems: NavEntry[] = [
  { to: '/dashboard', label: 'Dashboard', end: true, icon: DashboardIcon },
  { to: '/reminders', label: 'Reminders', icon: RemindersIcon },
  { to: '/outstanding-contacts', label: 'Contact issues', icon: OutstandingContactsIcon },
  { to: '/analytics', label: 'Analytics', icon: AnalyticsIcon },
  // Connections brings the invoices in, so it is one click away, not
  // buried in a closed group.
  { to: '/connections', label: 'Accounting connections', icon: ConnectionsIcon },
  { to: '/help', label: 'Help', icon: HelpIcon },
  { action: 'feedback', label: 'Feedback', icon: FeedbackIcon },
  {
    key: 'sending',
    label: 'Sending',
    icon: RulesIcon,
    items: [
      { to: '/rules', label: 'Reminder rules', icon: RulesIcon },
      { to: '/message-templates', label: 'Messages', icon: TemplatesIcon },
    ],
  },
  { to: '/settings', label: 'Settings', icon: SettingsIcon },
]

// Admin menu, in three groups: Informational (read-only: logs, stats,
// status), Settings (anything you can change; saves as you type) and Work
// (inboxes and one-off actions). Per-organisation settings stay with the
// organisation, under Work > Organisations.
const adminItems: NavEntry[] = [
  {
    key: 'admin-info',
    label: 'Overview',
    icon: AdminAnalyticsIcon,
    items: [
      { to: '/admin/analytics', label: 'Analytics', icon: AdminAnalyticsIcon },
      { to: '/admin/billing', label: 'Billing overview', icon: BillingIcon },
      { to: '/admin/customers', label: 'Customer directory', icon: CustomersIcon },
      { to: '/admin/reminders-today', label: "Today's reminders", icon: TodaysRemindersIcon },
      { to: '/admin/send-plan', label: 'Send plan', icon: TodaysRemindersIcon },
      { to: '/admin/integration-logs', label: 'Integration logs', icon: IntegrationLogsIcon },
      { to: '/admin/config-audit', label: 'Settings changes', icon: IntegrationLogsIcon },
      { to: '/admin/channels', label: 'Channels', icon: ChannelsIcon },
      {
        to: '/admin/whatsapp-template-submissions',
        label: 'WhatsApp org submissions',
        icon: WhatsAppTemplateSubmissionsIcon,
      },
      { to: '/admin/versions', label: 'Versions', icon: VersionsIcon },
    ],
  },
  {
    key: 'admin-settings',
    label: 'Configure',
    icon: SettingsIcon,
    items: [
      { to: '/admin/settings/scheduled-jobs', label: 'Scheduled jobs', icon: VersionsIcon },
      { to: '/admin/settings/plans-pricing', label: 'Plans and pricing', icon: BillingIcon },
      { to: '/admin/whatsapp-numbers', label: 'WhatsApp numbers', icon: WhatsAppNumbersIcon },
      {
        to: '/admin/whatsapp-platform-templates',
        label: 'WhatsApp templates',
        icon: WhatsAppPlatformTemplatesIcon,
      },
    ],
  },
  {
    key: 'admin-work',
    label: 'Operate',
    icon: AdminIcon,
    items: [
      { to: '/admin', label: 'Organisations', end: true, icon: AdminIcon },
      { to: '/admin/waitlist', label: 'Waitlist', icon: WaitlistIcon },
      { to: '/admin/practices', label: 'Practices', icon: CustomersIcon },
      { to: '/admin/feedback', label: 'Feedback', icon: FeedbackIcon },
      { to: '/admin/actions', label: 'Action list', icon: AdminActionsIcon },
      {
        to: '/admin/contact-inquiries',
        label: 'Contact inquiries',
        icon: ContactInquiriesIcon,
      },
      { to: '/admin/messaging', label: 'Messaging test', icon: MessagingTestIcon },
    ],
  },
]

const SIDEBAR_COLLAPSED_KEY = 'remindloop_sidebar_collapsed'
const NAV_GROUPS_OPEN_KEY = 'remindloop_nav_groups_open'

function navLinkClasses(collapsed: boolean, nested = false) {
  return ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-md text-sm font-medium transition-colors ${
      nested ? 'py-2 pl-3 pr-3' : 'px-3 py-2'
    } ${collapsed ? 'justify-center' : ''} ${
      // bg-sidebar-accent/text-sidebar-accent-foreground, not the raw
      // bg-brand-50/text-brand-700 pair this used to be -- brand-50 is a
      // light-only tint with no dark-mode value, which rendered the
      // active nav item as a stark near-white pill in an otherwise dark
      // sidebar (screenshot-verified, 2026-09-20 -- same root cause as
      // the marketing Home page's dark-mode bug fixed the same day).
      // sidebar-accent already carries a correct dark-mode pair.
      isActive ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'text-muted-foreground hover:bg-muted'
    }`
}

/**
 * A collapsible nav section. Auto-opens whenever one of its children is
 * the current route, so a deep link never lands you on a page whose menu
 * entry is hidden -- that auto-open is applied on top of the user's own
 * toggle rather than replacing it, so manually closing a group you're
 * inside still works.
 */
function NavGroupSection({
  group,
  collapsed,
  open,
  onToggle,
  onNavigate,
}: {
  group: NavGroup
  collapsed: boolean
  open: boolean
  onToggle: () => void
  onNavigate: () => void
}) {
  const Icon = group.icon
  const { pathname } = useLocation()
  const hasActiveChild = group.items.some(
    (item) => pathname === item.to || (!item.end && pathname.startsWith(`${item.to}/`)),
  )
  const expanded = open || hasActiveChild

  // Icon-only desktop mode has no room for a disclosure -- expanding the
  // sidebar is what reveals the children, so the group header just does
  // that instead of toggling something invisible.
  if (collapsed) {
    return (
      <button
        onClick={onToggle}
        title={group.label}
        aria-label={group.label}
        className={`flex items-center justify-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
          hasActiveChild ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'text-muted-foreground hover:bg-muted'
        }`}
      >
        <Icon />
      </button>
    )
  }

  return (
    <div>
      <button
        onClick={onToggle}
        aria-expanded={expanded}
        className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
          hasActiveChild ? 'text-foreground' : 'text-muted-foreground'
        } hover:bg-muted`}
      >
        <Icon />
        <span className="truncate">{group.label}</span>
        <span className="ml-auto">
          <ChevronIcon direction={expanded ? 'up' : 'down'} />
        </span>
      </button>
      {expanded && (
        // The rail is the only thing tying a child back to its parent once
        // you've scrolled the header off; indentation alone reads as
        // arbitrary whitespace on a narrow screen.
        <div className="ml-5 flex flex-col gap-0.5 border-l border-sidebar-border pl-2">
          {group.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={navLinkClasses(false, true)}
              onClick={onNavigate}
            >
              <span className="truncate">{item.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  )
}

export function AppShell({ children }: { children: ReactNode }) {
  const practiceSwitched = usePracticeSwitched()
  const { user, isImpersonating, logout } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const { pathname: titlePath } = useLocation()
  const { canSeeBilling } = useRole()
  const [pageTitle, setPageTitle] = useState('')
  const navigate = useNavigate()
  const settingsEntries = useSettingsEntries()
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  // Tab title follows the page heading ("Reminders · RemindLoop"). Pages
  // are lazy-loaded, so watch for the h1 rather than reading it once.
  useEffect(() => {
    const main = document.querySelector('main')
    if (!main) return
    const apply = () => {
      const h1 = main.querySelector('h1')?.textContent?.trim()
      if (h1) {
        document.title = `${h1} · RemindLoop`
        setPageTitle(h1)
      }
    }
    apply()
    const observer = new MutationObserver(apply)
    observer.observe(main, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [titlePath])
  // Escape closes the mobile drawer and returns focus to the menu button.
  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileOpen(false)
        menuButtonRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [mobileOpen])
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true',
  )
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem(NAV_GROUPS_OPEN_KEY) ?? '{}')
    } catch {
      // A hand-edited or half-written value must not take the whole shell
      // down -- fall back to everything closed.
      return {}
    }
  })

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed))
  }, [collapsed])

  useEffect(() => {
    localStorage.setItem(NAV_GROUPS_OPEN_KEY, JSON.stringify(openGroups))
  }, [openGroups])

  const toggleGroup = (key: string) =>
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }))

  // Not just user.is_system_admin: the backend's require_system_admin 403s
  // any request carrying an impersonation token regardless of the target's
  // own admin flag (closes a chaining hole -- see routes_admin.py), so an
  // admin impersonating a fellow admin must not see a link into a console
  // that would just 403 on every query.
  const showAdminLinks = user?.is_system_admin && !isImpersonating
  // The feedback panel is hidden while impersonating (submitting is
  // blocked server-side for the same reason other mutations are).
  const baseLinks = isImpersonating
    ? navItems.filter((entry) => !(isAction(entry) && entry.action === 'feedback'))
    : navItems
  // Message wording is locked to platform admins for now (backend enforces).
  const visibleBase = showAdminLinks
    ? baseLinks
    : baseLinks.map((entry) =>
        'items' in entry
          ? { ...entry, items: entry.items.filter((i) => i.to !== '/message-templates') }
          : entry,
      )
  const links = showAdminLinks ? [...visibleBase, ...adminItems] : visibleBase

  const [feedbackOpen, setFeedbackOpen] = useState(false)

  // The palette lists exactly what the sidebar shows this user (same
  // admin / impersonation filtering as `links` above), flattened.
  const paletteItems: PaletteItem[] = [
    ...links.flatMap((entry): PaletteItem[] => {
      if (isAction(entry)) return []
      const pages = isGroup(entry)
        ? entry.items.map((item) => ({ ...item, hint: entry.label }))
        : [{ ...entry, hint: undefined }]
      return pages.map((page) => ({
        id: `page-${page.to}`,
        label: page.label,
        hint: page.hint,
        section: 'Pages' as const,
        run: () => navigate(page.to),
      }))
    }),
    ...settingsEntries.map((entry) => ({
      id: `setting-${entry.section}-${entry.label}`,
      label: entry.label,
      hint: `Settings · ${entry.section}`,
      keywords: entry.keywords,
      section: 'Settings' as const,
      run: () =>
        navigate(`/settings?tab=${entry.tab}&find=${encodeURIComponent(entry.label)}&card=${encodeURIComponent(entry.section)}`),
    })),
    ...HELP_TOPICS.map((t) => ({
      id: `help-${t.slug}`,
      label: t.title,
      hint: 'Help',
      keywords: `help ${t.keywords}`,
      section: 'Help' as const,
      run: () => navigate(`/help/${t.slug}`),
    })),
    ...(isImpersonating
      ? []
      : [
          {
            id: 'action-feedback',
            label: 'Send feedback',
            section: 'Actions' as const,
            run: () => openFeedback(null, false),
          },
        ]),
    { id: 'action-theme', label: 'Toggle dark mode', keywords: 'theme light', section: 'Actions', run: toggleTheme },
    { id: 'action-logout', label: 'Log out', keywords: 'sign out', section: 'Actions', run: logout },
  ]
  const feedbackTriggerRef = useRef<HTMLElement | null>(null)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const { data: unread } = useQuery({
    queryKey: ['feedback-unread'],
    queryFn: () => api.get<{ count: number }>('/api/v1/feedback/unread-count'),
    enabled: Boolean(user) && !isImpersonating,
    refetchInterval: 5 * 60 * 1000,
  })
  const unreadCount = unread?.count ?? 0

  const openFeedback = (trigger: HTMLElement | null, fromMobileDrawer: boolean) => {
    feedbackTriggerRef.current = fromMobileDrawer ? menuButtonRef.current : trigger
    setMobileOpen(false)
    setFeedbackOpen(true)
  }
  const closeFeedback = () => {
    setFeedbackOpen(false)
    window.setTimeout(() => feedbackTriggerRef.current?.focus(), 0)
  }

  const sidebarContent = (
    <>
      <div className={`flex items-center gap-2 px-4 py-4 ${collapsed ? 'justify-center px-2' : ''}`}>
        {collapsed ? (
          <LogoMark size={22} reversed={theme === 'dark'} />
        ) : (
          <Logo size={22} reversed={theme === 'dark'} />
        )}
      </div>

      <div className="px-2 pb-2">
        <button
          type="button"
          onClick={() => {
            setMobileOpen(false)
            setPaletteOpen(true)
          }}
          aria-label="Search"
          title={collapsed ? 'Search (Ctrl+K)' : undefined}
          className={`flex w-full items-center gap-3 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted ${
            collapsed ? 'justify-center' : ''
          }`}
        >
          <SearchIcon />
          {!collapsed && (
            <>
              <span className="truncate">Search…</span>
              <kbd className="ml-auto rounded border border-border px-1.5 text-xs">Ctrl K</kbd>
            </>
          )}
        </button>
      </div>

      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-2">
        {links.map((entry) => {
          if (isGroup(entry)) {
            return (
              <NavGroupSection
                key={entry.key}
                group={entry}
                collapsed={collapsed}
                open={Boolean(openGroups[entry.key])}
                onToggle={() =>
                  collapsed ? setCollapsed(false) : toggleGroup(entry.key)
                }
                onNavigate={() => setMobileOpen(false)}
              />
            )
          }
          const Icon = entry.icon
          if (isAction(entry)) {
            return (
              <button
                key={entry.action}
                type="button"
                onClick={(e) => openFeedback(e.currentTarget, mobileOpen)}
                title={collapsed ? entry.label : undefined}
                aria-label={
                  unreadCount > 0 ? `${entry.label} (new reply)` : collapsed ? entry.label : undefined
                }
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted ${
                  collapsed ? 'justify-center' : ''
                }`}
              >
                <Icon />
                {!collapsed && <span className="truncate">{entry.label}</span>}
                {unreadCount > 0 && (
                  <span
                    data-testid="feedback-unread-dot"
                    className="ml-auto size-2 rounded-full bg-brand-500"
                  />
                )}
              </button>
            )
          }
          return (
            <NavLink
              key={entry.to}
              to={entry.to}
              end={entry.end}
              className={navLinkClasses(collapsed)}
              title={collapsed ? entry.label : undefined}
              onClick={() => setMobileOpen(false)}
            >
              <Icon />
              {!collapsed && <span className="truncate">{entry.label}</span>}
            </NavLink>
          )
        })}
      </nav>

      <div className="border-t border-border px-2 py-3">
        {!isImpersonating && <PracticeSwitcher activeOrgId={user?.org_id} collapsed={collapsed} />}
        {!collapsed && (
          <div className="px-2 pb-2 text-sm">
            <div data-testid="active-org-name" className="truncate font-medium text-foreground">
              {user?.org_name}
            </div>
            <div className="truncate text-xs text-muted-foreground">{user?.email}</div>
          </div>
        )}
        {canSeeBilling && !isImpersonating && (
          <NavLink
            to="/settings?tab=billing#billing"
            className={navLinkClasses(collapsed)}
            title={collapsed ? 'Plan and billing' : undefined}
            onClick={() => setMobileOpen(false)}
          >
            <SettingsIcon />
            {!collapsed && <span className="truncate">Plan and billing</span>}
          </NavLink>
        )}
        <div className={`flex items-center ${collapsed ? 'flex-col gap-1' : 'gap-1'}`}>
          <button
            onClick={logout}
            title={collapsed ? 'Log out' : undefined}
            className={`flex flex-1 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted ${
              collapsed ? 'justify-center' : ''
            }`}
          >
            <LogoutIcon />
            {!collapsed && <span>Log out</span>}
          </button>
          <ThemeToggle />
        </div>
        {/* Desktop-only collapse toggle -- mobile always shows the full
            drawer, collapsing doesn't apply there. */}
        <button
          onClick={() => setCollapsed((c) => !c)}
          className={`mt-1 hidden w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted md:flex ${
            collapsed ? 'justify-center' : ''
          }`}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <ChevronIcon direction={collapsed ? 'right' : 'left'} />
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </>
  )

  // The app uses client-side routing with a base URL, so a plain #hash jump
  // can be swallowed by the router. Move focus to <main> ourselves.
  function focusMain(e: ReactMouseEvent<HTMLAnchorElement>) {
    e.preventDefault()
    const main = document.getElementById('main-content')
    main?.focus()
    main?.scrollIntoView()
  }

  return (
    <div className="min-h-screen">
      <a href="#main-content" className="app-skip-link" onClick={focusMain}>
        Skip to content
      </a>
      <ImpersonationBanner />
      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        items={paletteItems}
        currency={user?.org_currency ?? 'ZAR'}
      />
      {!isImpersonating && (
        <FeedbackPanel open={feedbackOpen} onClose={closeFeedback} unreadCount={unreadCount} />
      )}

      {/* Mobile top bar. Menu button on the LEFT, next to the wordmark:
          it's the thumb-reachable corner on a phone, and it sits where the
          drawer it opens will appear from. */}
      <header className="sticky top-0 z-30 flex items-center gap-2 border-b border-sidebar-border bg-sidebar px-2 py-1.5 md:hidden">
        <button
          ref={menuButtonRef}
          onClick={() => setMobileOpen((open) => !open)}
          className="relative flex size-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
          aria-controls="mobile-nav"
        >
          {mobileOpen ? <CloseIcon /> : <MenuIcon />}
          {unreadCount > 0 && !mobileOpen && !isImpersonating && (
            <span
              data-testid="feedback-menu-dot"
              className="absolute right-1 top-1 size-2 rounded-full bg-brand-500"
            />
          )}
        </button>
        <Logo size={22} reversed={theme === 'dark'} />
        {pageTitle && (
          <span className="min-w-0 truncate text-sm font-semibold text-foreground" data-testid="mobile-page-title">
            {pageTitle}
          </span>
        )}
        <div className="ml-auto flex shrink-0 items-center">
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            aria-label="Search"
            className="flex size-11 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
          >
            <SearchIcon />
          </button>
          <ThemeToggle className="size-11" />
          {/* Org initial, standing in for an avatar -- there are no user
              images in this product, and an initial reads better at this
              size than a generic silhouette. */}
          <span
            className="ml-1 hidden size-8 items-center justify-center rounded-full border border-sidebar-border text-sm font-semibold text-muted-foreground min-[400px]:flex"
            title={user?.org_name ?? undefined}
            aria-hidden="true"
          >
            {(user?.org_name ?? '?').charAt(0).toUpperCase()}
          </span>
        </div>
      </header>

      {/* Mobile drawer + backdrop. Rendered whenever mobileOpen so the
          panel can transition in; the backdrop fades and the panel slides,
          which is what makes it read as a drawer rather than a flash. */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true">
          <div
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-[1px]"
            onClick={() => setMobileOpen(false)}
          />
          <aside
            id="mobile-nav"
            className="relative flex h-full w-72 max-w-[85vw] animate-in slide-in-from-left duration-200 flex-col border-r border-sidebar-border bg-sidebar shadow-xl"
          >
            {sidebarContent}
          </aside>
        </div>
      )}

      <div className="flex">
        {/* Desktop sidebar */}
        <aside
          className={`sticky top-0 hidden h-screen flex-col border-r border-sidebar-border bg-sidebar transition-[width] duration-150 md:flex ${
            collapsed ? 'w-16' : 'w-60'
          }`}
        >
          {sidebarContent}
        </aside>

        <main id="main-content" tabIndex={-1} className="min-w-0 flex-1 focus:outline-none">
          {/* Same gradient as the marketing masthead (Home.tsx) -- a
              deliberate echo, not a new motif, so the logged-in app reads
              as more of a piece with the brand instead of the near-
              colorless shell it was before (2026-09-22, user-requested
              after reviewing a vibrancy comparison). */}
          <div className="h-1 bg-gradient-to-r from-brand-500 via-brand-500 to-magenta-500" />
          <div className="px-4 py-6 sm:px-6 sm:py-8">
            <div className="mx-auto w-full max-w-[1120px]">
              {practiceSwitched ? <PracticeBillingBanner /> : <BillingNotice />}
              <SendingPausedBanner />
              {children}
              <div className="mt-10 text-center text-xs text-muted-foreground">
                <CookieSettingsLink className="min-h-11 px-3 underline underline-offset-2" />
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
